import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  BookCopy,
  BookOpenCheck,
  Check,
  CheckCircle2,
  ChevronRight,
  Clipboard,
  Copy,
  Download,
  FileJson,
  FileCheck2,
  FileText,
  Heart,
  LayoutTemplate,
  LibraryBig,
  LockKeyhole,
  PencilLine,
  Plus,
  Search,
  ShieldAlert,
  Sparkles,
  Trash2,
  TrendingUp,
  WandSparkles,
} from "lucide-react";
import {
  blueprints,
  interiorModules,
  marketPacks,
} from "../data/catalog";
import { interiorLayoutMasters } from "../data/expansion";
import {
  beginnerMarketplaceOptions,
  getMarketplaceGuide,
  isPromptRelevantToMarketplace,
  recommendedTrimSizes,
} from "../data/marketplaces";
import {
  composePrompt,
  allPrompts,
  channelPromptText,
  getPromptReadiness,
  promptCategories,
  promptSupportsBookTarget,
} from "../data/prompts";
import {
  createCatalogueRows,
  type CatalogueBook,
  type OfferSku,
  type PaperKingdomProject,
} from "../domain/schema";
import {
  copyText,
  downloadGrowthPackage,
  downloadCompleteHandoff,
  downloadProductionDossier,
  downloadProjectJson,
  downloadPromptPlan,
  composeCompleteHandoff,
} from "../lib/downloads";
import {
  getProjectCompletion,
  getProjectReadinessSummary,
} from "../lib/projectProgress";
import {
  extractInteriorRecommendation,
  extractTitleRecommendation,
} from "../lib/promptResults";
import {
  getPromptWorkForChannel,
  getPromptWorkKey,
  scopeLegacyPromptWork,
} from "../lib/promptWork";
import { usePortal } from "../state/PortalContext";
import { AppSidebarPortal } from "../components/AppShell";
import { ProjectNameDialog } from "../components/ProjectNameDialog";
import { LaunchGrowthPanel, ProductionPanel } from "./ExpansionPanels";
import {
  AppSelect,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  getInitials,
  OfferTierBadge,
  SectionHeading,
  TextArea,
  TextInput,
} from "../components/ui";

type StudioTab =
  | "brief"
  | "catalogue"
  | "prompts"
  | "production"
  | "growth"
  | "library";

function getStudioTabs(project: PaperKingdomProject): Array<{
  id: StudioTab;
  label: string;
  offer?: OfferSku;
  icon: React.ComponentType<{ size?: number }>;
}> {
  const isBook = getMarketplaceGuide(project.marketplace).usesKdpPrintSetup;
  return [
  { id: "brief", label: "Start here", icon: Clipboard },
  { id: "catalogue", label: isBook ? "Book ideas" : "Product ideas", icon: BookCopy },
  { id: "prompts", label: "Get prompts", icon: WandSparkles },
  { id: "production", label: isBook ? "Make the book" : "Make the files", offer: "paper-kingdom-production", icon: FileCheck2 },
  { id: "growth", label: "Prepare to sell", offer: "paper-kingdom-launch-growth", icon: TrendingUp },
  { id: "library", label: "Download & backup", icon: Download },
  ];
}

function hasAccess(entitlements: OfferSku[], required: OfferSku) {
  return entitlements.includes(required);
}

type WorkflowStage = {
  id: string;
  label: string;
  description: string;
  access?: string;
  icon: React.ComponentType<{ size?: number }>;
};

function returnToWorkflowTop() {
  window.requestAnimationFrame(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const target = document.querySelector(".workflow-navigator") ?? document.querySelector(".studio-panel");
    target?.scrollIntoView({
      behavior: reducedMotion ? "auto" : "smooth",
      block: "start",
    });
  });
}

function WorkflowNavigator({
  stages,
  activeStage,
  onChange,
}: {
  stages: WorkflowStage[];
  activeStage: string;
  onChange: (stageId: string) => void;
}) {
  const activeIndex = Math.max(0, stages.findIndex((stage) => stage.id === activeStage));
  const columns = { gridTemplateColumns: `repeat(${stages.length}, minmax(0, 1fr))` };

  return (
    <div className="workflow-navigator">
      <div className="workflow-progress" style={columns} aria-hidden="true">
        {stages.map((stage, index) => (
          <div
            key={stage.id}
            className={
              index < activeIndex
                ? "is-complete"
                : index === activeIndex
                  ? "is-active"
                  : "is-upcoming"
            }
          >
            <span>{index + 1}</span>
            <small>Step {index + 1}</small>
          </div>
        ))}
      </div>
      <div
        className="workflow-navigator__steps"
        role="tablist"
        aria-label="Tasks in this step"
        style={columns}
      >
        {stages.map((stage) => {
          const active = stage.id === activeStage;
          const Icon = stage.icon;
          return (
            <button
              key={stage.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={active ? "is-active" : ""}
              onClick={() => {
                onChange(stage.id);
                returnToWorkflowTop();
              }}
            >
              <span className="workflow-navigator__icon">
                <Icon size={16} />
              </span>
              <span>
                <strong>{stage.label}</strong>
              </span>
              {stage.access ? <Badge tone={active ? "green" : "clay"}>{stage.access}</Badge> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WorkflowContinue({
  label,
  onClick,
  disabled = false,
  help,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  help?: string;
}) {
  return (
    <div className="workflow-continue">
      <Button
        size="sm"
        disabled={disabled}
        onClick={() => {
          onClick();
          returnToWorkflowTop();
        }}
      >
        Next: {label} <ChevronRight size={15} />
      </Button>
      {help ? <p className="form-help">{help}</p> : null}
    </div>
  );
}

export function ProjectStudio({ projectId }: { projectId: string }) {
  const { projects, entitlements, updateProject } = usePortal();
  const [activeTab, setActiveTab] = useState<StudioTab>("brief");
  const [renameOpen, setRenameOpen] = useState(false);
  const project = projects.find((item) => item.id === projectId);

  if (!entitlements.includes("paper-kingdom-fe")) {
    return (
      <main className="narrow-page">
        <EmptyState
          icon={<LockKeyhole size={24} />}
          title="Paper Kingdom Core is off"
          description="This account does not have the Core tools enabled. An administrator can enable Core access from the Publisher menu."
        >
          <Link className="button button--secondary button--md" to="/PaperKingdom/studio">
            Back to Studio
          </Link>
        </EmptyState>
      </main>
    );
  }

  if (!project) {
    return (
      <main className="narrow-page">
        <EmptyState
          icon={<BookCopy size={24} />}
          title="This project is not in your workspace"
          description="It may belong to another account or have been removed from this device."
        >
          <Link className="button button--secondary button--md" to="/PaperKingdom/studio">
            Back to projects
          </Link>
        </EmptyState>
      </main>
    );
  }

  const setProject = (updater: (current: PaperKingdomProject) => PaperKingdomProject) =>
    updateProject(project.id, updater);

  const studioTabs = getStudioTabs(project);
  const entitledProgress = getProjectCompletion(project, entitlements);
  const progressByTab: Record<StudioTab, boolean> = {
    brief: entitledProgress.checks[0],
    catalogue: entitledProgress.checks[1],
    prompts: entitledProgress.checks[2],
    production: entitledProgress.checks[3],
    growth: entitledProgress.checks[4],
    library: true,
  };
  const completed = studioTabs.map((tab) => progressByTab[tab.id]);

  return (
    <>
      <AppSidebarPortal>
        <div className="project-sidebar">
          <Link className="back-link" to="/PaperKingdom/studio">
            <ArrowLeft size={16} /> Back to projects
          </Link>
          <div className="studio-project-title">
            <span className="studio-project-title__mark">
              {getInitials(project.imprint.name)}
            </span>
            <div>
              <small>Project name</small>
              <strong className="studio-project-title__name" title={project.name}>
                {project.name}
              </strong>
            </div>
            <button
              className="studio-project-title__rename"
              type="button"
              aria-label="Rename project"
              title="Rename project"
              onClick={() => setRenameOpen(true)}
            >
              <PencilLine size={15} />
            </button>
          </div>
          <div className="project-progress project-progress--lanes">
            <div>
              <span>{getMarketplaceGuide(project.marketplace).usesKdpPrintSetup ? "Book plan" : "Product plan"}</span>
              <strong>{entitledProgress.planning.percentage}%</strong>
            </div>
            <div className="progress-line">
              <span style={{ width: `${entitledProgress.planning.percentage}%` }} />
            </div>
            <small>{entitledProgress.production.label}</small>
            <small>{entitledProgress.publishing.label}</small>
          </div>
          <nav className="studio-nav" aria-label="Project sections">
            {studioTabs.map((tab, index) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={activeTab === tab.id ? "is-active" : ""}
                  onClick={() => {
                    setActiveTab(tab.id);
                    returnToWorkflowTop();
                  }}
                  aria-current={activeTab === tab.id ? "step" : undefined}
                >
                  <span className="studio-nav__number">
                    {completed[index] ? <Check size={14} /> : index + 1}
                  </span>
                  <Icon size={18} />
                  <span className="studio-nav__copy">
                    <span className="studio-nav__title">
                      <strong>{tab.label}</strong>
                      {tab.offer ? (
                        <OfferTierBadge
                          sku={tab.offer}
                          locked={!entitlements.includes(tab.offer)}
                        />
                      ) : null}
                    </span>
                  </span>
                  <ChevronRight size={16} />
                </button>
              );
            })}
          </nav>
        </div>
      </AppSidebarPortal>

      <main className="studio-layout">
        <section className="studio-canvas">
          {activeTab === "brief" ? (
            <BriefPanel
              project={project}
              entitlements={entitlements}
              setProject={setProject}
              onComplete={() => setActiveTab("catalogue")}
            />
          ) : null}
          {activeTab === "catalogue" ? (
            <CataloguePanel
              project={project}
              entitlements={entitlements}
              setProject={setProject}
              onComplete={() => setActiveTab("prompts")}
            />
          ) : null}
          {activeTab === "prompts" ? (
            <PromptsPanel
              project={project}
              entitlements={entitlements}
              setProject={setProject}
              onComplete={() => setActiveTab("production")}
            />
          ) : null}
          {activeTab === "production" ? (
            <ProductionPanel
              project={project}
              entitlements={entitlements}
              setProject={setProject}
              onComplete={() => setActiveTab("growth")}
            />
          ) : null}
          {activeTab === "growth" ? (
            <LaunchGrowthPanel
              project={project}
              entitlements={entitlements}
              setProject={setProject}
              onComplete={() => setActiveTab("library")}
            />
          ) : null}
          {activeTab === "library" ? (
            <LibraryPanel
              project={project}
              entitlements={entitlements}
              setProject={setProject}
            />
          ) : null}
        </section>
      </main>
      <ProjectNameDialog
        open={renameOpen}
        mode="rename"
        initialName={project.name}
        onCancel={() => setRenameOpen(false)}
        onConfirm={(name) => {
          setProject((current) => ({ ...current, name }));
          setRenameOpen(false);
        }}
      />
    </>
  );
}

function BriefPanel({
  project,
  entitlements,
  setProject,
  onComplete,
}: {
  project: PaperKingdomProject;
  entitlements: OfferSku[];
  setProject: (updater: (project: PaperKingdomProject) => PaperKingdomProject) => void;
  onComplete: () => void;
}) {
  const [activeStage, setActiveStage] = useState<"details" | "style">("details");
  const [confirmExample, setConfirmExample] = useState(false);
  const [pendingMarketplace, setPendingMarketplace] = useState<{
    marketplace: PaperKingdomProject["marketplace"];
    format: PaperKingdomProject["format"];
  } | null>(null);
  const savedPack = marketPacks.find((pack) => pack.id === project.marketPackId);
  const activePack = savedPack && hasAccess(entitlements, savedPack.requiredOffer)
    ? savedPack
    : marketPacks.find((pack) => hasAccess(entitlements, pack.requiredOffer)) ?? marketPacks[0];
  const availableMarketCount = marketPacks.filter((pack) =>
    hasAccess(entitlements, pack.requiredOffer),
  ).length;
  const marketplaceGuide = getMarketplaceGuide(project.marketplace);
  const isBook = marketplaceGuide.usesKdpPrintSetup;
  const itemNoun = isBook ? "book" : "product";
  const itemPlural = isBook ? "books" : "products";
  const applyMarketplace = (
    marketplace: PaperKingdomProject["marketplace"],
    format: PaperKingdomProject["format"],
  ) =>
    setProject((current) => ({
      ...current,
      marketplace,
      format,
      bookType: format,
      promptWork: scopeLegacyPromptWork(
        current.promptWork,
        current.marketplace,
      ),
      catalogue: current.catalogue.map((book) => ({ ...book, type: format })),
      production: {
        ...current.production,
        trimSize:
          format === current.format
            ? current.production.trimSize
            : recommendedTrimSizes[format],
      },
    }));
  const applyExample = () =>
    setProject((current) => ({
      ...current,
      marketPackId: activePack.id,
      audience: activePack.audience,
      theme: activePack.promise,
    }));
  const continueToBookIdea = () => {
    setProject((current) => ({
      ...current,
      catalogue: current.catalogue.map((book, index) =>
        index === 0 && !book.differentiator.trim()
          ? { ...book, differentiator: current.theme }
          : book,
      ),
    }));
    onComplete();
  };

  return (
    <div className="studio-panel">
      {activeStage === "details" ? (
        <div className="workflow-stage" role="tabpanel">
          <SectionHeading
            eyebrow="Start here"
            title={`Tell us about the ${itemNoun} in everyday language`}
            description="You do not need publishing terms. A simple description is enough to begin."
          />
          <div className="beginner-help-link">
            <Button variant="secondary" size="sm" onClick={() => setActiveStage("style")}>
              <Sparkles size={15} /> I need a ready-made example
            </Button>
          </div>
          <div className="form-card">
            <div className="form-grid form-grid--2">
              <Field label="Where will you sell it first?" hint="This changes the file and listing guidance you see.">
                <AppSelect
                  value={project.marketplace}
                  onChange={(event) => {
                    const marketplace = event.target.value as PaperKingdomProject["marketplace"];
                    const guide = getMarketplaceGuide(marketplace);
                    const format = guide.formatOptions.includes(project.format)
                      ? project.format
                      : guide.formatOptions[0];
                    if (format !== project.format) {
                      setPendingMarketplace({ marketplace, format });
                      return;
                    }
                    applyMarketplace(marketplace, format);
                  }}
                >
                  {[
                    ...beginnerMarketplaceOptions,
                    ...(project.marketplace === "Multi-channel"
                      ? (["Multi-channel"] as const)
                      : []),
                  ].map((option) => <option key={option}>{option}</option>)}
                </AppSelect>
              </Field>
              <Field label="What are you making?" hint="Choose the closest match. You can change it later.">
                <AppSelect
                  value={project.format}
                  onChange={(event) => {
                    const format = event.target.value as PaperKingdomProject["format"];
                    setProject((current) => ({
                      ...current,
                      format,
                      bookType: format,
                      catalogue: current.catalogue.map((book) => ({ ...book, type: format })),
                      production: {
                        ...current.production,
                        trimSize: recommendedTrimSizes[format],
                      },
                    }));
                  }}
                >
                  {marketplaceGuide.formatOptions.map((option) => <option key={option}>{option}</option>)}
                </AppSelect>
              </Field>
            </div>
            <Field label={`Who is this ${itemNoun} for?`} hint="Say who they are and what feels difficult right now.">
              <TextInput
                placeholder="Example: Busy parents who want calmer, less rushed mornings"
                value={project.audience}
                onChange={(event) => setProject((current) => ({ ...current, audience: event.target.value }))}
              />
            </Field>
            <Field label={`What should the ${itemNoun} help them do?`} hint="Describe a useful action or experience, not a guaranteed result.">
              <TextInput
                placeholder="Example: Plan the morning in ten quiet minutes"
                value={project.theme}
                onChange={(event) => setProject((current) => ({ ...current, theme: event.target.value }))}
              />
            </Field>
            <Field label="Anything to keep in mind? (optional)" hint="Tone, things to avoid, source material, or a must-have idea.">
              <TextArea
                rows={3}
                maxLength={5000}
                placeholder="Example: Keep it friendly and simple. Do not make health or income promises."
                value={project.notes}
                onChange={(event) =>
                  setProject((current) => ({ ...current, notes: event.target.value }))
                }
              />
            </Field>
          </div>
          <WorkflowContinue
            label={`Name your ${itemNoun}`}
            onClick={continueToBookIdea}
          />
        </div>
      ) : null}

      {activeStage === "style" ? (
        <section className="workflow-stage" role="tabpanel">
          <SectionHeading
            eyebrow="Need a starting point?"
            title="Use a ready-made example"
            description="Choose one example to fill the reader and goal for you."
            actions={(
              <Button variant="ghost" size="sm" onClick={() => setActiveStage("details")}>
                <ArrowLeft size={15} /> Back to my idea
              </Button>
            )}
          />
          <div className="choice-selector">
            <div className="choice-selector__control">
              <Field label={`Example audience and ${itemNoun} idea`}>
                <AppSelect
                  aria-label={`Example audience and ${itemNoun} idea`}
                  value={activePack.id}
                  onChange={(event) => {
                    const pack = marketPacks.find((item) => item.id === event.target.value);
                    if (!pack || !hasAccess(entitlements, pack.requiredOffer)) return;
                    setProject((current) => ({ ...current, marketPackId: pack.id }));
                  }}
                >
                  {marketPacks.map((pack) => (
                    <option
                      key={pack.id}
                      value={pack.id}
                      disabled={!hasAccess(entitlements, pack.requiredOffer)}
                    >
                      {pack.name}
                      {!hasAccess(entitlements, pack.requiredOffer) &&
                      pack.requiredOffer === "paper-kingdom-unlimited"
                        ? " · OTO 1"
                        : ""}
                    </option>
                  ))}
                </AppSelect>
              </Field>
              <small>
                {availableMarketCount} examples are available to use.
                {availableMarketCount < marketPacks.length
                  ? " Grayed-out options are marked OTO 1."
                  : ""}
              </small>
            </div>
            <article className="choice-profile">
              <header>
                <div>
                  <span className="eyebrow">Example only</span>
                  <h3>{activePack.name}</h3>
                </div>
              </header>
              <div className="choice-profile__facts">
                <div>
                  <span>Who it is for</span>
                  <p>{activePack.audience}</p>
                </div>
                <div>
                  <span>What the {itemPlural} help with</span>
                  <p>{activePack.promise}</p>
                </div>
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (project.audience.trim() || project.theme.trim()) {
                    setConfirmExample(true);
                    return;
                  }
                  applyExample();
                  setActiveStage("details");
                }}
              >
                Use this example
              </Button>
            </article>
          </div>
        </section>
      ) : null}
      <ConfirmDialog
        open={confirmExample}
        title={`Replace your reader and goal with “${activePack.name}”?`}
        description="This will replace the reader and goal you already entered."
        confirmLabel="Replace my answers"
        onCancel={() => setConfirmExample(false)}
        onConfirm={() => {
          applyExample();
          setConfirmExample(false);
          setActiveStage("details");
        }}
      />
      <ConfirmDialog
        open={pendingMarketplace !== null}
        title={`Change to ${pendingMarketplace?.marketplace ?? "this sales channel"}?`}
        description={`Your current ${project.format} format is not available there. Paper Kingdom will change it to ${pendingMarketplace?.format ?? "a compatible format"}. Your book wording will be kept.`}
        confirmLabel="Change channel and format"
        onCancel={() => setPendingMarketplace(null)}
        onConfirm={() => {
          if (pendingMarketplace) {
            applyMarketplace(
              pendingMarketplace.marketplace,
              pendingMarketplace.format,
            );
          }
          setPendingMarketplace(null);
        }}
      />
    </div>
  );
}

function ImprintPanel({
  project,
  setProject,
  onComplete,
}: {
  project: PaperKingdomProject;
  setProject: (updater: (project: PaperKingdomProject) => PaperKingdomProject) => void;
  onComplete: () => void;
}) {
  const [activeStage, setActiveStage] = useState<"identity" | "rules">("identity");
  const updateImprint = (patch: Partial<PaperKingdomProject["imprint"]>) =>
    setProject((current) => ({ ...current, imprint: { ...current.imprint, ...patch } }));

  return (
    <div className="studio-panel">
      <WorkflowNavigator
        activeStage={activeStage}
        onChange={(stageId) => setActiveStage(stageId as typeof activeStage)}
        stages={[
          { id: "identity", label: "Brand identity", description: "Name, positioning & colour", access: "Core", icon: BookOpenCheck },
          { id: "rules", label: "Shared rules", description: "Cover, interior, voice & metadata", access: "Core", icon: ShieldAlert },
        ]}
      />

      {activeStage === "identity" ? (
        <div className="workflow-stage" role="tabpanel">
          <SectionHeading
            eyebrow="Task 1"
            title="Shape the brand identity"
          />
          <div className="imprint-preview">
            <div
              className="imprint-preview__mark"
              style={{ background: project.imprint.primaryColor, color: project.imprint.secondaryColor }}
            >
              <span>{project.imprint.name.slice(0, 1) || "P"}</span>
              <i style={{ background: project.imprint.accentColor }} />
            </div>
            <div>
              <span className="eyebrow">Live imprint card</span>
              <h2>{project.imprint.name || "Name your imprint"}</h2>
              <p>{project.imprint.positioning || "Your positioning statement will appear here."}</p>
              <div className="palette-preview">
                {[project.imprint.primaryColor, project.imprint.secondaryColor, project.imprint.accentColor].map((color) => (
                  <span key={color} style={{ background: color }} title={color}>{color}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="form-card">
            <div className="form-grid form-grid--2">
              <Field label="Imprint name">
                <TextInput value={project.imprint.name} onChange={(event) => updateImprint({ name: event.target.value })} />
              </Field>
              <Field label="Typography mood">
                <TextInput value={project.imprint.typographyMood} onChange={(event) => updateImprint({ typographyMood: event.target.value })} />
              </Field>
            </div>
            <Field label="Positioning" hint="Audience + publishing territory + useful promise + reason to trust the approach.">
              <TextArea rows={3} value={project.imprint.positioning} onChange={(event) => updateImprint({ positioning: event.target.value })} />
            </Field>
            <div className="form-grid form-grid--3">
              {[
                ["Primary colour", "primaryColor"],
                ["Paper colour", "secondaryColor"],
                ["Accent colour", "accentColor"],
              ].map(([label, key]) => (
                <Field label={label} key={key}>
                  <div className="color-field">
                    <input
                      type="color"
                      value={project.imprint[key as keyof typeof project.imprint]}
                      onChange={(event) => updateImprint({ [key]: event.target.value })}
                    />
                    <TextInput
                      value={project.imprint[key as keyof typeof project.imprint]}
                      onChange={(event) => updateImprint({ [key]: event.target.value })}
                    />
                  </div>
                </Field>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {activeStage === "rules" ? (
        <div className="workflow-stage" role="tabpanel">
          <SectionHeading
            eyebrow="Task 2"
            title="Set the reusable rules"
          />
          <div className="rules-grid">
            {[
              ["Cover composition rules", "coverRules", "What stays fixed across every cover? What is allowed to vary?"],
              ["Interior rules", "interiorRules", "Define rhythm, density, writing space, navigation, and accessibility."],
              ["Voice conventions", "voiceRules", "How should instructions, reflection, promotion, and caution sound?"],
              ["Metadata conventions", "metadataRules", "Define accuracy, hierarchy, repetition, and claim boundaries."],
            ].map(([label, key, hint]) => (
              <Field label={label} hint={hint} key={key}>
                <TextArea
                  rows={5}
                  value={project.imprint[key as keyof typeof project.imprint]}
                  onChange={(event) => updateImprint({ [key]: event.target.value })}
                />
              </Field>
            ))}
          </div>
          <div className="quality-note">
            <Sparkles size={20} />
            <div>
              <strong>The lock is a creative advantage.</strong>
              <p>
                Strong shared constraints remove repeated decisions. Title-specific reader value,
                subject, examples, and focal imagery should still change meaningfully.
              </p>
            </div>
          </div>
          <WorkflowContinue label="Plan books" onClick={onComplete} />
        </div>
      ) : null}
    </div>
  );
}

function CataloguePanel({
  project,
  entitlements,
  setProject,
  onComplete,
}: {
  project: PaperKingdomProject;
  entitlements: OfferSku[];
  setProject: (updater: (project: PaperKingdomProject) => PaperKingdomProject) => void;
  onComplete: () => void;
}) {
  const [activeStage, setActiveStage] = useState<"matrix" | "blueprints">("matrix");
  const [pendingBlueprintId, setPendingBlueprintId] = useState<string | null>(null);
  const [pendingRemoveBookId, setPendingRemoveBookId] = useState<string | null>(null);
  const isBook = getMarketplaceGuide(project.marketplace).usesKdpPrintSetup;
  const itemNoun = isBook ? "book" : "product";
  const updateBook = (id: string, patch: Partial<CatalogueBook>) =>
    setProject((current) => ({
      ...current,
      catalogue: current.catalogue.map((book) => (book.id === id ? { ...book, ...patch } : book)),
    }));

  const addBook = () =>
    setProject((current) => {
      const count = Math.min(current.catalogue.length + 1, 24);
      return {
        ...current,
        catalogueSize: count,
        catalogue: createCatalogueRows(count, current.bookType, current.theme || "Title", current.catalogue),
      };
    });

  const applyBlueprint = (id: string) => {
    const blueprint = blueprints.find((item) => item.id === id);
    if (!blueprint) return;
    setProject((current) => ({
      ...current,
      name: blueprint.name,
      theme: blueprint.positioning,
      catalogueSize: blueprint.titles.length,
      catalogue: blueprint.titles.map((book, index) => ({
        id: crypto.randomUUID(),
        sequence: index + 1,
        type: current.bookType,
        title: book.title,
        subtitle: book.subtitle,
        differentiator: book.differentiator,
        theme: book.title,
        status: "planned",
      })),
    }));
  };

  return (
    <div className="studio-panel">
      {activeStage === "matrix" ? (
        <div className="workflow-stage" role="tabpanel">
          <SectionHeading
            eyebrow={`Your ${itemNoun}`}
            title={`Give it a simple working name`}
            description={`The name can be rough. The next step can help you improve it.`}
          />
          <div className="beginner-help-link">
            <Button variant="secondary" size="sm" onClick={() => setActiveStage("blueprints")}>
              <LayoutTemplate size={15} /> Use a ready-made plan
              <OfferTierBadge
                sku="paper-kingdom-dfy"
                locked={!entitlements.includes("paper-kingdom-dfy")}
              />
            </Button>
          </div>
          <div className="simple-catalogue-list">
            {project.catalogue.map((book) => (
              <article className="simple-catalogue-card" key={book.id}>
                {project.catalogue.length > 1 ? (
                  <header>
                    <strong>{itemNoun} {book.sequence}</strong>
                    <button
                      className="icon-button"
                      aria-label={`Remove ${itemNoun} ${book.sequence}`}
                      onClick={() => setPendingRemoveBookId(book.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </header>
                ) : null}
                <Field
                  label="Working title (optional)"
                  hint="Leave it blank if you want the title prompt to help."
                >
                  <TextInput
                    aria-label={`Title ${book.sequence}`}
                    placeholder="Example: The Home Plumbing Record"
                    value={book.title}
                    onChange={(event) => updateBook(book.id, { title: event.target.value, theme: event.target.value })}
                  />
                </Field>
                <Field label={`What will someone use this ${itemNoun} for?`}>
                  <TextArea
                    aria-label={`Distinct purpose ${book.sequence}`}
                    rows={3}
                    placeholder="Example: Record a problem clearly before speaking to a qualified professional."
                    value={book.differentiator}
                    onChange={(event) => updateBook(book.id, { differentiator: event.target.value })}
                  />
                </Field>
              </article>
            ))}
          </div>
          <div className="catalogue-secondary-actions">
            <Button
              variant="secondary"
              size="sm"
              onClick={addBook}
              disabled={project.catalogue.length >= 24}
            >
              <Plus size={16} /> Add another {itemNoun}
            </Button>
            <WorkflowContinue label="Get my prompts" onClick={onComplete} />
          </div>
        </div>
      ) : null}

      {activeStage === "blueprints" ? (
      <section className="workflow-stage" role="tabpanel">
          <SectionHeading
            eyebrow="Ready-made plans"
            title="Choose one only if you need a starting point"
            description={`Using a plan replaces your current ${itemNoun} idea.`}
            actions={(
              <Button variant="ghost" size="sm" onClick={() => setActiveStage("matrix")}>
                <ArrowLeft size={15} /> Back to my {itemNoun}
              </Button>
            )}
        />
        <div className="blueprint-grid">
          {blueprints.map((blueprint) => {
            const locked = !hasAccess(entitlements, blueprint.requiredOffer);
            return (
              <article key={blueprint.id} className={locked ? "is-locked" : ""}>
                <div className="blueprint-card__top">
                  <Badge tone="clay">{blueprint.market}</Badge>
                  <OfferTierBadge sku={blueprint.requiredOffer} locked={locked} />
                </div>
                <h3>{blueprint.name}</h3>
                <p>{blueprint.positioning}</p>
                <ol>{blueprint.titles.slice(0, 3).map((book) => <li key={book.title}>{book.title}</li>)}</ol>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={locked}
                  onClick={() => setPendingBlueprintId(blueprint.id)}
                >
                  {locked ? <LockKeyhole size={15} /> : <LayoutTemplate size={15} />}
                  {locked ? `Ready-made ${itemNoun} plans required` : `Use this ${itemNoun} plan`}
                </Button>
              </article>
            );
          })}
        </div>
      </section>
      ) : null}
      <ConfirmDialog
        open={pendingBlueprintId !== null}
        title={`Replace your current ${itemNoun} ideas with “${
          blueprints.find((item) => item.id === pendingBlueprintId)?.name ??
          "this ready-made plan"
        }”?`}
        description={`This changes the project name, goal, and complete ${itemNoun} list. Cancel to keep your current work.`}
        confirmLabel={`Use this ${itemNoun} plan`}
        onCancel={() => setPendingBlueprintId(null)}
        onConfirm={() => {
          if (pendingBlueprintId) {
            applyBlueprint(pendingBlueprintId);
            setActiveStage("matrix");
          }
          setPendingBlueprintId(null);
        }}
      />
      <ConfirmDialog
        open={pendingRemoveBookId !== null}
        title={`Remove “${
          project.catalogue.find((book) => book.id === pendingRemoveBookId)?.title ||
          `this ${itemNoun} idea`
        }”?`}
        description={`This removes its name, purpose, ${isBook ? "cover" : "preview"} plan, page plan, and listing records from this project.`}
        confirmLabel={`Remove ${itemNoun} idea`}
        onCancel={() => setPendingRemoveBookId(null)}
        onConfirm={() => {
          if (pendingRemoveBookId) {
            setProject((current) => {
              const catalogue = current.catalogue
                .filter((item) => item.id !== pendingRemoveBookId)
                .map((item, index) => ({ ...item, sequence: index + 1 }));
              const coverPlans = { ...current.production.coverPlans };
              const interiorPlans = { ...current.production.interiorPlans };
              const listings = { ...current.launchGrowth.listings };
              delete coverPlans[pendingRemoveBookId];
              delete interiorPlans[pendingRemoveBookId];
              delete listings[pendingRemoveBookId];
              const listingsByMarketplace = Object.fromEntries(
                Object.entries(current.launchGrowth.listingsByMarketplace).map(
                  ([marketplace, channelListings]) => {
                    const nextListings = { ...channelListings };
                    delete nextListings[pendingRemoveBookId];
                    return [marketplace, nextListings];
                  },
                ),
              );
              return {
                ...current,
                catalogueSize: Math.max(1, catalogue.length),
                catalogue,
                production: {
                  ...current.production,
                  coverPlans,
                  interiorPlans,
                },
                launchGrowth: {
                  ...current.launchGrowth,
                  listings,
                  listingsByMarketplace,
                },
              };
            });
          }
          setPendingRemoveBookId(null);
        }}
      />
    </div>
  );
}

function beginnerPromptExample(category: string): string {
  const examples: Record<string, string> = {
    "Titles & Subtitles":
      "I like the idea, but I am not sure the name is clear. Suggest easier names and explain your choices.",
    "Cover Concepts":
      "Give me a simple, professional cover idea and leave enough room for the title.",
    "Interior Systems":
      "I don't know what pages this book needs. Make it useful, simple, and not repetitive.",
    "Product Descriptions":
      "Write an easy-to-understand product description. Explain what the buyer gets and do not make big promises.",
    "Keywords & Categories":
      "Give me search phrase ideas, but clearly mark anything I need to check on the marketplace.",
    "Listing & Metadata":
      "Help me write an honest sales page using only the features that are really in my product.",
    "Cover Production":
      "Turn my cover idea into clear instructions for the person or tool making the final cover.",
    "Interior Production":
      "Help me turn my page ideas into a complete customer-ready file.",
    "Print & Preflight":
      "Explain what I still need to check before I upload or sell this product.",
    "Launch Strategy":
      "Give me a small launch checklist. Leave out advertising and email unless I ask for them.",
  };
  return examples[category] ?? "Use simple words and give me the finished result. Do not end with questions or extra next steps.";
}

function friendlyPromptCategory(category: string): string {
  const names: Record<string, string> = {
    "Imprint Strategy": "Publishing identity",
    "Market Intelligence": "Audience ideas",
    "Catalogue Architecture": "Product collection",
    "Titles & Subtitles": "Product names",
    "Cover Concepts": "Visual ideas",
    "Interior Systems": "Pages & back cover",
    "Back-cover Copy": "Buyer-facing copy",
    "Product Descriptions": "Draft positioning",
    "Keywords & Categories": "Search and categories",
    "Promotion & Quality": "Promotion and checks",
    "Cover Production": "Final visual",
    "Interior Production": "Final product pages",
    "Print & Preflight": "Final file checks",
    "Rights & Handoff": "Rights and handoff",
    "Listing & Metadata": "Sales page",
    "A+ Content": "Amazon extra content",
    "Launch Strategy": "Launch checklist",
    "Reader Funnel": "Optional reader follow-up",
    "Cross-sell & Retention": "Optional related products",
  };
  return names[category] ?? category;
}

function beginnerPromptTitle(title: string): string {
  const names: Record<string, string> = {
    "Outcome-led title set": "Improve the product name",
    "Hero cover routes": "Create the cover artwork",
    "Interior architecture": "Plan the pages and back cover",
    "Marketplace description": "Draft how to explain the product",
    "Complete listing package": "Finish the product listing",
  };
  return names[title] ?? title;
}

function beginnerPromptDescription(
  prompt: (typeof allPrompts)[number],
  project: PaperKingdomProject,
): string {
  if (prompt.id === "pk-interior-01") {
    return channelPromptText(
      "Create the inside-page plan, page order, and ready-to-use back-cover text in one answer.",
      project,
    );
  }
  return channelPromptText(prompt.objective, project);
}

function PromptsPanel({
  project,
  entitlements,
  setProject,
  onComplete,
}: {
  project: PaperKingdomProject;
  entitlements: OfferSku[];
  setProject: (updater: (project: PaperKingdomProject) => PaperKingdomProject) => void;
  onComplete: () => void;
}) {
  const availablePrompts = useMemo(
    () =>
      allPrompts.filter(
        (prompt) =>
          hasAccess(entitlements, prompt.requiredOffer) &&
          isPromptRelevantToMarketplace(prompt.category, project.marketplace),
      ),
    [entitlements, project.marketplace],
  );
  const availableCategories = useMemo(
    () => promptCategories.filter((item) => availablePrompts.some((prompt) => prompt.category === item)),
    [availablePrompts],
  );
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>("All categories");
  const [browserMode, setBrowserMode] = useState<"library" | "plan">("plan");
  const [selectedId, setSelectedId] = useState(
    project.promptPlanIds.find((id) => availablePrompts.some((prompt) => prompt.id === id)) ??
      availablePrompts[0]?.id ??
      allPrompts[0].id,
  );
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const [downloadState, setDownloadState] = useState<"idle" | "started">("idle");
  const [applyNotice, setApplyNotice] = useState("");
  const [previousTitle, setPreviousTitle] = useState<{
    bookId: string;
    title: string;
    subtitle: string;
  } | null>(null);
  const selected =
    availablePrompts.find((prompt) => prompt.id === selectedId) ??
    availablePrompts[0] ??
    allPrompts[0];
  const customization = project.promptCustomizations?.[selected.id] ?? {
    targetBookId: project.catalogue.length === 1 ? project.catalogue[0]?.id : undefined,
    customInstructions: "",
  };
  const promptWork = getPromptWorkForChannel(project, selected.id) ?? {
    copiedAt: "",
    response: "",
    status: "not-started" as const,
    approvedAt: "",
    useInLaterSteps: false,
  };
  const composed = composePrompt(selected, project, customization);
  const readiness = getPromptReadiness(selected, project, customization);
  const isBook = getMarketplaceGuide(project.marketplace).usesKdpPrintSetup;
  const itemNoun = isBook ? "book" : "product";
  const targetBook =
    project.catalogue.find((book) => book.id === customization.targetBookId) ??
    project.catalogue[0];
  const hasWorkingTitle = Boolean(targetBook?.title.trim());
  const titleRecommendation = extractTitleRecommendation(promptWork.response);
  const interiorRecommendation = extractInteriorRecommendation(promptWork.response);
  const recommendedLayouts = interiorRecommendation.layoutIds
    .map((id) => interiorLayoutMasters.find((layout) => layout.id === id)?.name)
    .filter(Boolean);
  const recommendedModules = interiorRecommendation.moduleIds
    .map((id) => interiorModules.find((module) => module.id === id)?.name)
    .filter(Boolean);
  const appliedInteriorPlan = targetBook
    ? project.production.interiorPlans[targetBook.id]
    : undefined;
  const appliedCoverPlan = targetBook
    ? project.production.coverPlans[targetBook.id]
    : undefined;
  const hasInteriorRecommendation = Boolean(
    interiorRecommendation.layoutIds.length ||
      interiorRecommendation.moduleIds.length ||
      interiorRecommendation.sequenceNotes ||
      interiorRecommendation.backCoverCopy,
  );
  const interiorRecommendationApplied = Boolean(
    hasInteriorRecommendation &&
      targetBook &&
      interiorRecommendation.layoutIds.every((id) =>
        appliedInteriorPlan?.layoutIds.includes(id),
      ) &&
      interiorRecommendation.moduleIds.every((id) =>
        appliedInteriorPlan?.moduleIds.includes(id),
      ) &&
      (!interiorRecommendation.sequenceNotes ||
        appliedInteriorPlan?.sequenceNotes === interiorRecommendation.sequenceNotes) &&
      (!interiorRecommendation.backCoverCopy ||
        appliedCoverPlan?.backCoverCopy === interiorRecommendation.backCoverCopy),
  );
  const filtered = useMemo(
    () =>
      availablePrompts.filter(
        (prompt) =>
          (category === "All categories" || prompt.category === category) &&
          `${prompt.title} ${prompt.objective}`.toLowerCase().includes(query.toLowerCase()),
      ),
    [availablePrompts, category, query],
  );
  const planPrompts = useMemo(
    () =>
      project.promptPlanIds
        .map((id) => availablePrompts.find((prompt) => prompt.id === id))
        .filter((prompt): prompt is (typeof availablePrompts)[number] => Boolean(prompt)),
    [availablePrompts, project.promptPlanIds],
  );
  const visiblePrompts = browserMode === "plan" ? planPrompts : filtered;

  useEffect(() => {
    if (visiblePrompts.length && !visiblePrompts.some((prompt) => prompt.id === selectedId)) {
      setSelectedId(visiblePrompts[0].id);
    }
  }, [selectedId, visiblePrompts]);

  useEffect(() => {
    setApplyNotice("");
  }, [selectedId]);

  const togglePlan = (id: string) =>
    setProject((current) => ({
      ...current,
      promptPlanIds: current.promptPlanIds.includes(id)
        ? current.promptPlanIds.filter((item) => item !== id)
        : [...current.promptPlanIds, id],
    }));

  const toggleFavorite = (id: string) =>
    setProject((current) => ({
      ...current,
      favoritePromptIds: current.favoritePromptIds.includes(id)
        ? current.favoritePromptIds.filter((item) => item !== id)
        : [...current.favoritePromptIds, id],
    }));

  const updateCustomization = (
    patch: Partial<{
      targetBookId: string | undefined;
      customInstructions: string;
    }>,
  ) =>
    setProject((current) => ({
      ...current,
      promptCustomizations: {
        ...(current.promptCustomizations ?? {}),
        [selected.id]: {
          ...current.promptCustomizations?.[selected.id],
          ...patch,
          customInstructions:
            patch.customInstructions ??
            current.promptCustomizations?.[selected.id]?.customInstructions ??
            "",
        },
      },
    }));

  const updatePromptWork = (
    patch: Partial<PaperKingdomProject["promptWork"][string]>,
  ) =>
    setProject((current) => {
      const scopedKey = getPromptWorkKey(selected.id, current.marketplace);
      const { [selected.id]: legacyWork, ...otherPromptWork } =
        current.promptWork;
      return {
        ...current,
        promptWork: {
          ...otherPromptWork,
          [scopedKey]: {
            ...(current.promptWork[scopedKey] ?? legacyWork ?? {
            copiedAt: "",
            response: "",
            status: "not-started" as const,
            approvedAt: "",
            useInLaterSteps: false,
          }),
            ...patch,
          },
        },
      };
    });

  const changeCategory = (nextCategory: string) => {
    setCategory(nextCategory);
    const firstMatch = availablePrompts.find(
      (prompt) => nextCategory === "All categories" || prompt.category === nextCategory,
    );
    if (firstMatch) setSelectedId(firstMatch.id);
  };

  const doCopy = async () => {
    try {
      await copyText(composed);
      updatePromptWork({ copiedAt: new Date().toISOString() });
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }
    window.setTimeout(() => setCopyState("idle"), 1800);
  };

  const doDownloadPlan = () => {
    downloadPromptPlan(project);
    setDownloadState("started");
    window.setTimeout(() => setDownloadState("idle"), 4_000);
  };

  const applyRecommendedTitle = () => {
    if (!targetBook || !titleRecommendation.title) return;
    setPreviousTitle({
      bookId: targetBook.id,
      title: targetBook.title,
      subtitle: targetBook.subtitle,
    });
    setProject((current) => ({
      ...current,
      catalogue: current.catalogue.map((book) =>
        book.id === targetBook.id
          ? {
              ...book,
              title: titleRecommendation.title,
              theme: titleRecommendation.title,
              subtitle: titleRecommendation.subtitle || book.subtitle,
            }
          : book,
      ),
    }));
    setApplyNotice("The recommended title is now used throughout the project.");
  };

  const undoRecommendedTitle = () => {
    if (!previousTitle) return;
    setProject((current) => ({
      ...current,
      catalogue: current.catalogue.map((book) =>
        book.id === previousTitle.bookId
          ? {
              ...book,
              title: previousTitle.title,
              theme: previousTitle.title,
              subtitle: previousTitle.subtitle,
            }
          : book,
      ),
    }));
    setPreviousTitle(null);
    setApplyNotice("The previous title has been restored.");
  };

  const applyRecommendedInterior = () => {
    if (
      !targetBook ||
      (!interiorRecommendation.layoutIds.length &&
        !interiorRecommendation.moduleIds.length &&
        !interiorRecommendation.sequenceNotes &&
        !interiorRecommendation.backCoverCopy)
    ) {
      return;
    }
    setProject((current) => {
      const existing = current.production.interiorPlans[targetBook.id] ?? {
        layoutIds: [],
        moduleIds: [],
        sequenceNotes: "",
      };
      const existingCover = current.production.coverPlans[targetBook.id] ?? {
        variant: "",
        titleTreatment: "",
        subtitleTreatment: "",
        focalBrief: "",
        backCoverCopy: "",
        differentiationNote: "",
      };
      return {
        ...current,
        production: {
          ...current.production,
          interiorPlans: {
            ...current.production.interiorPlans,
            [targetBook.id]: {
              layoutIds: interiorRecommendation.layoutIds.length
                ? interiorRecommendation.layoutIds
                : existing.layoutIds,
              moduleIds: interiorRecommendation.moduleIds.length
                ? interiorRecommendation.moduleIds
                : existing.moduleIds,
              sequenceNotes:
                interiorRecommendation.sequenceNotes || existing.sequenceNotes,
            },
          },
          coverPlans: {
            ...current.production.coverPlans,
            [targetBook.id]: {
              ...existingCover,
              backCoverCopy:
                interiorRecommendation.backCoverCopy || existingCover.backCoverCopy,
            },
          },
        },
      };
    });
    setApplyNotice(
      `The page plan and ${isBook ? "back-cover" : "preview"} text are ready in Make the ${isBook ? "book" : "files"}.`,
    );
  };

  const approvePromptAnswer = () => {
    if (promptWork.useInLaterSteps) {
      updatePromptWork({ useInLaterSteps: false });
      return;
    }
    updatePromptWork({
      status: "approved",
      approvedAt: new Date().toISOString(),
      useInLaterSteps: true,
    });
    if (selected.category === "Interior Systems") {
      applyRecommendedInterior();
    } else if (selected.category === "Titles & Subtitles" && titleRecommendation.title) {
      applyRecommendedTitle();
    }
  };

  return (
    <div className="studio-panel">
      <section className="beginner-callout prompt-guide" aria-labelledby="prompt-guide-title">
        <div>
          <Badge tone="green">How this works</Badge>
          <h2 id="prompt-guide-title">Paper Kingdom prepares the instructions for you</h2>
          <p>
            Choose what you want to create, add a personal request if you have one, and copy the
            finished prompt into ChatGPT or another AI tool. Paper Kingdom does not generate the
            answer on this page.
          </p>
        </div>
        <ol>
          <li><strong>1</strong><span>Choose a prompt</span></li>
          <li><strong>2</strong><span>Add an optional request</span></li>
          <li><strong>3</strong><span>Copy and use it</span></li>
          <li><strong>4</strong><span>Paste back the useful answer</span></li>
        </ol>
      </section>
      <div className="prompt-studio">
        <aside className="prompt-browser">
          <div className="prompt-browser__modes" role="tablist" aria-label="Prompt browser">
            <button
              type="button"
              role="tab"
              aria-selected={browserMode === "library"}
              className={browserMode === "library" ? "is-active" : ""}
              onClick={() => setBrowserMode("library")}
            >
              Advanced prompt library
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={browserMode === "plan"}
              className={browserMode === "plan" ? "is-active" : ""}
              onClick={() => setBrowserMode("plan")}
            >
              Recommended prompts <span>{project.promptPlanIds.length}</span>
            </button>
          </div>

          {browserMode === "library" ? (
            <div className="prompt-browser__filters">
              <label className="search-input">
                <Search size={17} />
                <input
                  aria-label="Search prompts"
                  placeholder="Search tasks such as title, cover, pages, or listing"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </label>
              <AppSelect
                value={category}
                onChange={(event) => changeCategory(event.target.value)}
                aria-label="Prompt category"
              >
                <option>All categories</option>
                {availableCategories.map((item) => (
                  <option key={item} value={item}>{friendlyPromptCategory(item)}</option>
                ))}
              </AppSelect>
            </div>
          ) : null}

          <div className="prompt-browser__count">
            <span>{visiblePrompts.length} {browserMode === "plan" ? "recommended prompts" : "prompts found"}</span>
            <span>{browserMode === "plan" ? "Suggested order" : `${project.promptPlanIds.length} in your list`}</span>
          </div>
          <div className="prompt-list">
            {visiblePrompts.map((prompt, index) => {
              const inPlan = project.promptPlanIds.includes(prompt.id);
              const work = getPromptWorkForChannel(project, prompt.id);
              return (
                <div className={`prompt-list__row ${selected.id === prompt.id ? "is-active" : ""}`} key={prompt.id}>
                  <button
                    type="button"
                    className="prompt-list__select"
                    onClick={() => setSelectedId(prompt.id)}
                  >
                    <span className="prompt-number">{index + 1}</span>
                    <span><strong>{beginnerPromptTitle(prompt.title)}</strong><small>{friendlyPromptCategory(prompt.category)}</small></span>
                    {work?.status === "skipped" ? (
                      <Badge tone="neutral">Not needed</Badge>
                    ) : inPlan ? (
                      <CheckCircle2 size={16} />
                    ) : (
                      <ChevronRight size={15} />
                    )}
                  </button>
                </div>
              );
            })}
            {!visiblePrompts.length ? (
              <div className="prompt-list__empty">
                {browserMode === "plan" ? "No recommended prompts yet." : "No prompts match this search."}
              </div>
            ) : null}
          </div>
        </aside>
        {!visiblePrompts.length ? (
          <article className="prompt-output prompt-output--empty">
            <EmptyState
              icon={<WandSparkles size={22} />}
              title={browserMode === "plan" ? "Choose a prompt" : "No task matches that search"}
              description={
                browserMode === "plan"
                  ? `Browse the advanced library and keep only the instructions that help with the ${itemNoun} you are making now.`
                  : `Search for the job you need, such as title, ${isBook ? "cover" : "preview"}, pages, description, or listing. Your ${itemNoun} topic is already included automatically.`
              }
            >
              {browserMode === "plan" ? (
                <Button onClick={() => setBrowserMode("library")}>Open advanced prompt library</Button>
              ) : null}
            </EmptyState>
          </article>
        ) : (
        <article className="prompt-output">
          <header className="prompt-output__header">
            <div>
              <Badge tone="clay">{friendlyPromptCategory(selected.category)}</Badge>
              <h2>{beginnerPromptTitle(selected.title)}</h2>
              <p>{beginnerPromptDescription(selected, project)}</p>
            </div>
            <button
              className={`favorite-button ${project.favoritePromptIds.includes(selected.id) ? "is-active" : ""}`}
              onClick={() => toggleFavorite(selected.id)}
              aria-label={project.favoritePromptIds.includes(selected.id) ? "Remove from favorites" : "Add to favorites"}
            >
              <Heart size={19} fill="currentColor" />
            </button>
          </header>

          <div className="prompt-context">
            <div className="prompt-context__status">
              <div>
                <span>Project details included automatically</span>
                <strong>{readiness.included.join(" · ")}</strong>
              </div>
              <Badge tone={readiness.missing.length ? "gold" : "green"}>
                {readiness.missing.length
                  ? `${readiness.missing.length} ${readiness.missing.length === 1 ? "thing" : "things"} to add`
                  : "Enough to continue"}
              </Badge>
            </div>
            {readiness.missing.length ? (
              <p className="prompt-context__gaps">{readiness.missing.join(" · ")}</p>
            ) : null}
            <div className="prompt-context__fields">
              {promptSupportsBookTarget(selected.category) && project.catalogue.length > 1 ? (
                <Field label="Which book is this for?">
                  <AppSelect
                    value={customization.targetBookId ?? ""}
                    onChange={(event) =>
                      updateCustomization({ targetBookId: event.target.value || undefined })
                    }
                    aria-label="Prompt scope"
                  >
                    <option value="">All book ideas</option>
                    {project.catalogue.map((book) => (
                      <option key={book.id} value={book.id}>
                        {book.sequence}. {book.title || "Untitled book"}
                      </option>
                    ))}
                  </AppSelect>
                </Field>
              ) : null}
              <Field
                label="Add a personal request (optional)"
                hint="Use ordinary words. Paper Kingdom will add the detailed instructions for you."
              >
                <TextArea
                  rows={3}
                  maxLength={1500}
                  placeholder={beginnerPromptExample(selected.category)}
                  value={customization.customInstructions}
                  onChange={(event) =>
                    updateCustomization({ customInstructions: event.target.value })
                  }
                />
              </Field>
            </div>
          </div>

          <div className="prompt-toolbar">
            <Button
              variant={project.promptPlanIds.includes(selected.id) ? "secondary" : "primary"}
              size="sm"
              onClick={() => togglePlan(selected.id)}
            >
              {project.promptPlanIds.includes(selected.id) ? <Check size={15} /> : <Plus size={15} />}
              {project.promptPlanIds.includes(selected.id) ? "In recommended prompts" : "Add to recommended prompts"}
            </Button>
            <Button variant="secondary" size="sm" onClick={doCopy}>
              {copyState === "copied" ? <Check size={15} /> : <Copy size={15} />}
              {copyState === "copied" ? "Prompt copied" : copyState === "error" ? "Copy failed" : "Copy prompt"}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!project.promptPlanIds.length}
              onClick={doDownloadPlan}
            >
              {downloadState === "started" ? <Check size={16} /> : <Download size={16} />}
              {downloadState === "started"
                ? "Download started"
                : `Download prompts (${project.promptPlanIds.length})`}
            </Button>
          </div>
          <section className="prompt-result-bridge" aria-labelledby="prompt-result-title">
            <div>
              <Badge tone="green">After ChatGPT answers</Badge>
              <h3 id="prompt-result-title">Bring the useful answer back</h3>
              <p>
                Paste the answer here so it stays with this project, can guide
                later prompts, and appears in the final complete handoff.
              </p>
            </div>
            <Field
              label="Paste the current answer (optional)"
              hint="Keep only the answer you want to review. Paper Kingdom does not create a hidden history."
            >
              <TextArea
                rows={8}
                maxLength={50000}
                placeholder="Copy the answer from ChatGPT or another AI tool and paste it here."
                value={promptWork.response}
                onChange={(event) =>
                  updatePromptWork({
                    response: event.target.value,
                    status: event.target.value.trim()
                      ? "needs-review"
                      : "not-started",
                    approvedAt: "",
                    useInLaterSteps: false,
                  })
                }
              />
            </Field>
            <div className="prompt-result-bridge__actions">
              <Badge
                tone={
                  promptWork.status === "approved"
                    ? "green"
                    : promptWork.status === "skipped"
                      ? "neutral"
                    : promptWork.response.trim()
                      ? "gold"
                      : "neutral"
                }
              >
                {promptWork.status === "approved"
                  ? "Reviewed"
                  : promptWork.status === "skipped"
                    ? "Not needed"
                  : promptWork.response.trim()
                    ? "Needs your review"
                    : "No answer saved"}
              </Badge>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  promptWork.status === "skipped"
                    ? updatePromptWork({ status: "not-started" })
                    : updatePromptWork({
                        status: "skipped",
                        approvedAt: "",
                        useInLaterSteps: false,
                      })
                }
              >
                {promptWork.status === "skipped"
                  ? "Use this prompt"
                  : "Not needed for this project"}
              </Button>
              <Button
                size="sm"
                variant={promptWork.useInLaterSteps ? "secondary" : "primary"}
                disabled={!promptWork.response.trim()}
                onClick={approvePromptAnswer}
              >
                {promptWork.useInLaterSteps ? <Check size={15} /> : <ChevronRight size={15} />}
                {promptWork.useInLaterSteps
                  ? "Stop using in later steps"
                  : selected.category === "Interior Systems"
                    ? `Review and add to Make the ${isBook ? "book" : "files"}`
                    : selected.category === "Titles & Subtitles"
                      ? `Review and use this ${isBook ? "title" : "name"}`
                      : "Review and use in later steps"}
              </Button>
            </div>
            {promptWork.status === "approved" &&
            promptWork.useInLaterSteps &&
            selected.category === "Titles & Subtitles" ? (
              <div className="apply-result-card">
                <div>
                  <Badge tone={hasWorkingTitle && targetBook?.title === titleRecommendation.title ? "green" : "clay"}>
                    {hasWorkingTitle && targetBook?.title === titleRecommendation.title ? "Added to project" : "Ready to apply"}
                  </Badge>
                  <h4>
                    {hasWorkingTitle && targetBook?.title === titleRecommendation.title
                      ? `Your ${isBook ? "title" : "name"} is ready`
                      : `Use the reviewed ${isBook ? "title" : "name"} in the project`}
                  </h4>
                  {titleRecommendation.title ? (
                    hasWorkingTitle && targetBook?.title === titleRecommendation.title ? (
                      <p>
                        <strong>{titleRecommendation.title}</strong>
                        {titleRecommendation.subtitle ? ` — ${titleRecommendation.subtitle}` : ""}
                      </p>
                    ) : (
                      <p>
                        <span>{targetBook?.title || `Untitled ${itemNoun}`}</span>
                        <ChevronRight size={15} />
                        <strong>{titleRecommendation.title}</strong>
                        {titleRecommendation.subtitle ? ` — ${titleRecommendation.subtitle}` : ""}
                      </p>
                    )
                  ) : (
                    <small>
                      This answer does not contain a “Recommended title:” line.
                      Ask the AI to keep the Paper Kingdom handoff at the end,
                      then paste the updated answer here.
                    </small>
                  )}
                </div>
                <div className="apply-result-card__actions">
                  <Button
                    size="sm"
                    disabled={!titleRecommendation.title || (hasWorkingTitle && targetBook?.title === titleRecommendation.title)}
                    onClick={applyRecommendedTitle}
                  >
                    {hasWorkingTitle && targetBook?.title === titleRecommendation.title
                      ? `${isBook ? "Title" : "Name"} added`
                      : `Use this ${isBook ? "title" : "name"}`}
                  </Button>
                  {previousTitle ? (
                    <Button size="sm" variant="secondary" onClick={undoRecommendedTitle}>
                      Undo
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}
            {promptWork.status === "approved" &&
            promptWork.useInLaterSteps &&
            selected.category === "Interior Systems" ? (
              <div className="apply-result-card">
                <div>
                  <Badge tone={interiorRecommendationApplied ? "green" : "clay"}>
                    {interiorRecommendationApplied ? `Added to Make the ${isBook ? "book" : "files"}` : "Ready to add"}
                  </Badge>
                  <h4>Your pages and {isBook ? "back-cover" : "preview"} text are prepared</h4>
                  {hasInteriorRecommendation ? (
                    <ul>
                      {recommendedLayouts.length || recommendedModules.length ? (
                        <li>
                          <strong>Pages:</strong>{" "}
                          {[...recommendedLayouts, ...recommendedModules].join(", ")}
                        </li>
                      ) : null}
                      {interiorRecommendation.sequenceNotes ? (
                        <li><strong>Page flow:</strong> {interiorRecommendation.sequenceNotes}</li>
                      ) : null}
                      {interiorRecommendation.backCoverCopy ? (
                        <li><strong>{isBook ? "Back-cover" : "Preview"} text:</strong> {interiorRecommendation.backCoverCopy}</li>
                      ) : null}
                    </ul>
                  ) : (
                    <small>
                      This answer does not contain the Paper Kingdom page-plan
                      lines. Ask the AI to keep the handoff shown in the prompt,
                      then paste the updated answer.
                    </small>
                  )}
                </div>
                {!interiorRecommendationApplied ? (
                  <Button
                    size="sm"
                    disabled={!hasInteriorRecommendation}
                    onClick={applyRecommendedInterior}
                  >
                    Use this plan
                  </Button>
                ) : null}
              </div>
            ) : null}
            {applyNotice ? <p className="form-help form-help--success" role="status">{applyNotice}</p> : null}
          </section>
          <details className="prompt-preview">
            <summary>
              <span>
                <strong>View the full prompt</strong>
                <small>This is what will be copied. You usually do not need to change it.</small>
              </span>
              <ChevronRight size={17} />
            </summary>
            <pre className="prompt-text">{composed}</pre>
          </details>
        </article>
        )}
      </div>
      <WorkflowContinue
        label={isBook ? "Make the book" : "Make the files"}
        onClick={onComplete}
        disabled={!hasWorkingTitle}
        help={
          hasWorkingTitle
            ? undefined
            : `Use the first prompt above. Paper Kingdom will add the recommended ${isBook ? "title" : "name"} automatically.`
        }
      />
    </div>
  );
}

function LibraryPanel({
  project,
  entitlements,
  setProject,
}: {
  project: PaperKingdomProject;
  entitlements: OfferSku[];
  setProject: (updater: (project: PaperKingdomProject) => PaperKingdomProject) => void;
}) {
  const [exportNotice, setExportNotice] = useState<
    | ""
    | "complete"
    | "prompt"
    | "project"
    | "production"
    | "selling"
    | "copied"
    | "copy-error"
  >("");
  const readiness = getProjectReadinessSummary(project, entitlements);
  const remainingCount = readiness.reduce(
    (total, section) => total + section.missing.length,
    0,
  );
  const startExport = (
    kind: "complete" | "prompt" | "project" | "production" | "selling",
  ) => {
    if (kind === "complete") downloadCompleteHandoff(project, entitlements);
    if (kind === "prompt") downloadPromptPlan(project);
    if (kind === "project") downloadProjectJson(project);
    if (kind === "production") downloadProductionDossier(project);
    if (kind === "selling") downloadGrowthPackage(project);
    if (kind === "complete") {
      setProject((current) => ({
        ...current,
        lastCompleteHandoffAt: new Date().toISOString(),
      }));
    }
    setExportNotice(kind);
    window.setTimeout(() => setExportNotice(""), 4_000);
  };
  const copyEverything = async () => {
    try {
      await copyText(composeCompleteHandoff(project, entitlements));
      setProject((current) => ({
        ...current,
        lastCompleteHandoffAt: new Date().toISOString(),
      }));
      setExportNotice("copied");
    } catch {
      setExportNotice("copy-error");
    }
    window.setTimeout(() => setExportNotice(""), 4_000);
  };

  return (
    <div className="studio-panel">
      <div className="workflow-stage" role="tabpanel">
        <SectionHeading
          eyebrow="Download and backup"
          title="Take the complete project with you"
          description="Everything from the earlier steps is gathered here. You do not need to return to another page to collect prompts, answers, book-making notes, or selling notes."
        />
        <div className="beginner-callout">
          <strong>
            {remainingCount
              ? `${remainingCount} item${remainingCount === 1 ? "" : "s"} still need attention.`
              : "Every required item is recorded."}
          </strong>
          <p>
            The complete handoff includes the unfinished checklist as well as
            all work already completed, so nothing is hidden on an earlier page.
          </p>
        </div>
        <div className="final-readiness-grid">
          {readiness.map((section) => (
            <article className={section.complete ? "is-complete" : ""} key={section.title}>
              <span>{section.complete ? <Check size={15} /> : section.missing.length}</span>
              <div>
                <strong>{section.title}</strong>
                <small>
                  {section.complete
                    ? "Complete"
                    : `${section.missing.length} item${section.missing.length === 1 ? "" : "s"} included in the final checklist`}
                </small>
              </div>
            </article>
          ))}
        </div>
        <article className="complete-handoff-card">
          <div>
            <Badge tone="green">Recommended</Badge>
            <h3>Complete project handoff</h3>
            <p>
              One readable document with the final checklist, reviewed AI
              answers, prepared prompts, cover and page plans, customer-file
              notes, rights checks, listing, and launch notes.
            </p>
          </div>
          <div>
            <Button onClick={() => startExport("complete")}>
              {exportNotice === "complete" ? <Check size={16} /> : <Download size={16} />}
              {exportNotice === "complete" ? "Download started" : "Download everything"}
            </Button>
            <Button variant="secondary" onClick={() => void copyEverything()}>
              {exportNotice === "copied" ? <Check size={16} /> : <Copy size={16} />}
              {exportNotice === "copied"
                ? "Copied"
                : exportNotice === "copy-error"
                  ? "Copy failed — use download"
                  : "Copy everything"}
            </Button>
          </div>
        </article>
        <div className="export-grid">
          <article>
            <span className="export-icon"><FileJson size={22} /></span>
            <Badge>Technical backup</Badge>
            <h3>Project backup for re-importing</h3>
            <p>A JSON copy for restoring the project in Paper Kingdom. Use the complete handoff above for reading.</p>
            <Button onClick={() => startExport("project")}>
              {exportNotice === "project" ? <Check size={16} /> : <Download size={16} />}
              {exportNotice === "project" ? "Download started" : "Download project backup"}
            </Button>
          </article>
          <article>
            <span className="export-icon"><FileText size={22} /></span>
            <Badge>Prompts</Badge>
            <h3>Prompt document</h3>
            <p>{project.promptPlanIds.length} prompts prepared with the current project information.</p>
            <Button
              variant="secondary"
              onClick={() => startExport("prompt")}
              disabled={!project.promptPlanIds.length}
            >
              {exportNotice === "prompt" ? <Check size={16} /> : <Download size={16} />}
              {exportNotice === "prompt" ? "Download started" : "Download prompts"}
            </Button>
          </article>
          {entitlements.includes("paper-kingdom-production") ? (
            <article>
              <span className="export-icon"><FileCheck2 size={22} /></span>
              <Badge>Book files</Badge>
              <h3>Production handoff</h3>
              <p>Cover, inside-page, file, rights, proof and final-check notes.</p>
              <Button variant="secondary" onClick={() => startExport("production")}>
                {exportNotice === "production" ? <Check size={16} /> : <Download size={16} />}
                {exportNotice === "production" ? "Download started" : "Download production notes"}
              </Button>
            </article>
          ) : null}
          {entitlements.includes("paper-kingdom-launch-growth") ? (
            <article>
              <span className="export-icon"><TrendingUp size={22} /></span>
              <Badge>Selling</Badge>
              <h3>Selling kit</h3>
              <p>Product-page copy, promotion copy, image plan, publishing steps and any optional follow-up work.</p>
              <Button variant="secondary" onClick={() => startExport("selling")}>
                {exportNotice === "selling" ? <Check size={16} /> : <Download size={16} />}
                {exportNotice === "selling" ? "Download started" : "Download selling kit"}
              </Button>
            </article>
          ) : null}
        </div>
        <div className="workflow-continue">
          <Link className="button button--primary button--sm" to="/PaperKingdom/studio">
            Finish: Back to projects <ChevronRight size={15} />
          </Link>
        </div>
      </div>
    </div>
  );
}
